import {
  isRazorpayPayload,
  openRazorpayCheckout,
  type RazorpayPaymentPayload,
} from '@/lib/razorpay';

/**
 * Taking an online payment on the storefront, wherever it is asked for: an
 * order, a booking's deposit or fee, or a pack of sessions.
 *
 * Two pages open the gateway: the till, the moment an order is placed, and the
 * pay page a shopper is sent to from a WhatsApp chat. Both need the same three
 * things - the gateway shown, the shopper's answer turned into which message
 * they read next, and a gateway that will not load treated as "not paid yet"
 * rather than as a lost order. Written once here so the two cannot drift.
 *
 * Nothing that comes back is proof of payment. The order is confirmed by the
 * signed webhook the provider sends our server; this only decides what the
 * shopper is told while that arrives. See `lib/razorpay.ts`.
 */

/** What the shopper did with the gateway, as the confirmation page reads it. */
export type RazorpayOutcome = 'paid' | 'awaiting';

export interface CollectPaymentArgs {
  payment: RazorpayPaymentPayload;
  /** Rupees, as the decimal string the API returned. */
  amount: string;
  currency?: string;
  storeName: string;
  /**
   * What is being paid for, as a reference: the order number for an order,
   * the booking or package purchase id otherwise. Shown as "Order <number>"
   * unless `description` says what it is.
   */
  orderNumber: string;
  /** Checkout's line under the store name - "Haircut, 5 June". Defaults to "Order <number>". */
  description?: string;
  customer: { name: string; email: string; phone?: string };
  themeColor?: string;
  /**
   * Told the provider's payment id when the gateway says the shopper paid, for
   * a caller that asks the server to check it with the provider (a booking's
   * `/payment/verify`). Orders leave it off and wait for the webhook.
   */
  onPaymentId?: (paymentId: string) => void;
}

/**
 * Open the gateway and wait for the shopper's answer.
 *
 * Resolves rather than rejects on every path: `'paid'` when the gateway says
 * the shopper went through, `'awaiting'` when they closed it or it could not
 * be reached at all. The order exists and is unpaid in both of the latter
 * cases, which is recoverable, and the page that called should say so rather
 * than imply the order was lost.
 */
export function collectRazorpayPayment({
  onPaymentId,
  ...args
}: CollectPaymentArgs): Promise<RazorpayOutcome> {
  return new Promise((resolve) => {
    openRazorpayCheckout({
      ...args,
      onPaid: (response) => {
        if (response?.razorpay_payment_id) onPaymentId?.(response.razorpay_payment_id);
        resolve('paid');
      },
      onDismissed: () => resolve('awaiting'),
    }).catch(() => resolve('awaiting'));
  });
}

export { isRazorpayPayload };
